import { Paragraph, Link, Highlight } from '@/components/common/Announcement/components'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { SUPPORT_URL } from '@/config/settings'
import { useNavigate } from "@tanstack/react-router";
import {PropsWithChildren} from "react";
import {Button} from "@/components/common/Button";
import {useAnnouncementsContext} from "@/contexts/AnnouncementsContext";

const types = {
  default: 'default',
  error: 'error',
  warning: 'warning',
}

const titles = {
  default: 'Beep Boop',
  error: 'Error',
  warning: 'Warning',
}

const priorities = {
  low: 0,
  medium: 1,
  high: 2
}

export enum ErrorEnum {
    UNKNOWN = 'UNKNOWN',
    FOUR_O_FOUR = 'FOUR_O_FOUR',
    SERVER_ERROR = 'SERVER_ERROR',
    DEVICE_NOT_SUPPORTED = 'DEVICE_NOT_SUPPORTED',
    WEBVIEW_NOT_SUPPORTED = 'WEBVIEW_NOT_SUPPORTED',
    BROWSER_NOT_SUPPORTED = 'BROWSER_NOT_SUPPORTED',
    NOT_OFFICIALLY_SUPPORTED = 'NOT_OFFICIALLY_SUPPORTED',
    HOST_CONNECTION_ERROR = 'HOST_CONNECTION_ERROR',
    NO_INTERNET_CONNECTION = 'NO_INTERNET_CONNECTION',
    PROFILE_LOAD_ERROR = 'PROFILE_LOAD_ERROR'
}

export interface AnnouncementType extends PropsWithChildren {
    id: ErrorEnum
    type?: string
    title?: string
    modal?: boolean
    subTitle?: string
}

const NoInternetConnectionError = () => {

    return (
        <>
            <Paragraph>
                Make sure you are connected to the internet in order to run GameGlass. Check your internet connection.
            </Paragraph>

            <Button
                primary
                onClick={() => window.location.reload()}
            >
                Retry
            </Button>
        </>
    )
}

const HostConnectionError = () => {
    const navigate = useNavigate()
    const { data: user } = useCurrentUser()
    const { removeAnnouncement } = useAnnouncementsContext()

    return (
        <>
            <Paragraph>
                Make sure your GameGlass Host is running on your PC.
                <br/>
                You can download the Host from gameglass.gg website on your PC.
                <br/>
                {(user && user.username) && (
                    <>
                        You are currently logged in as <Highlight>{user.username}</Highlight>.
                    </>
                )}
            </Paragraph>

            <Button
                primary
                onClick={() => window.location.reload()}
            >
                Retry
            </Button>

            <Button
                outline
                onClick={() => {
                    removeAnnouncement(ErrorEnum.HOST_CONNECTION_ERROR)
                    navigate({ to: '/logout' })
                }}
            >
                Logout
            </Button>

            <Link
                href={`${SUPPORT_URL}/en/articles/7231972-cannot-connect-to-host-error`}
            >
                Get help
            </Link>
        </>
    )
}

// Shown by AuthIntermediator once the profile request has exhausted its retries.
// Retry re-runs the query in place rather than reloading, so a recovered
// connection resolves without losing the current route.
const ProfileLoadError = () => {
    const navigate = useNavigate()
    const { refetch } = useCurrentUser()

    return (
        <>
            <Paragraph>
                We could not load your GameGlass profile.
                <br/>
                Check your internet connection and try again.
                <br/>
                If this keeps happening, please contact support.
            </Paragraph>

            <Button
                primary
                onClick={() => void refetch()}
            >
                Retry
            </Button>

            <Button
                outline
                onClick={() => navigate({ to: '/logout' })}
            >
                Logout
            </Button>

            <Link
                href={SUPPORT_URL}
            >
                Get help
            </Link>
        </>
    )
}

const NotOfficiallySupported = () => {
    const { removeAnnouncement } = useAnnouncementsContext()

    return (
        <>
            <Paragraph>
                {'This device or browser does not meet the official support requirements.'}
                <br/>
                {'If you proceed, be advised, you may experience some issues while running GameGlass.'}
            </Paragraph>

            <Link
                href={`${SUPPORT_URL}/en/articles/7231929-minimum-requirements`}
            >
                {'Get help'}
            </Link>

            <Button
                primary
                onClick={() => removeAnnouncement(ErrorEnum.NOT_OFFICIALLY_SUPPORTED)}
            >
                {'Proceed'}
            </Button>
        </>
    )
}

export const ANNOUNCEMENTS : Record<ErrorEnum, AnnouncementType> = Object.freeze({
  [ErrorEnum.UNKNOWN]: {
    id: ErrorEnum.UNKNOWN,
    type: types.error,
    title: titles.error,
      modal: false,
    subTitle: 'Something went wrong.',
    children: (
      <>
        <Paragraph>
          {'Page not found'}
        </Paragraph>
        <Button
          primary
          onClick={() => (window.location.href = location.origin)}
        >
          {'Retry'}
        </Button>
      </>
    )
  },
  [ErrorEnum.FOUR_O_FOUR]: {
    id: ErrorEnum.FOUR_O_FOUR,
    type: types.error,
    title: titles.error,
      modal: false,
    subTitle: '404',
    children: (
      <>
        <Paragraph>
          {'Page not found'}
        </Paragraph>
        <Button
          primary
          onClick={() => (window.location.href = location.origin)}
        >
          {'Retry'}
        </Button>
      </>
    )
  },
  [ErrorEnum.SERVER_ERROR]: {
    id: ErrorEnum.SERVER_ERROR,
    type: types.error,
    title: titles.error,
      modal: false,
    subTitle: 'Servers offline',
    children: (
        <>
          <Paragraph>
            {'The GameGlass servers are temporarily offline.'}
            <br/>
            {'We are working on getting the servers back online.'}
            <br/>
            {'You can check the status of our services here.'}
          </Paragraph>

          <Button
            primary
            onClick={() => (window.location.href = location.origin)}
          >
            {'Retry'}
          </Button>

          <Link
            href={`${SUPPORT_URL}/en/articles/7231677-gameglass-service-disruptions`}
          >
            {'Get help'}
          </Link>
        </>
      )
  },
  [ErrorEnum.DEVICE_NOT_SUPPORTED]: {
    id: ErrorEnum.DEVICE_NOT_SUPPORTED,
    type: types.error,
    title: titles.error,
      modal: false,
      subTitle: 'Compatibility issue',
    children: (
        <>
          <Paragraph>
            {'Unfortunately, this device is not supported by GameGlass.'}
            <br/>
            {'You can find a list of supported devices and operating systems on our support page.'}
          </Paragraph>
          <Link
            href={`${SUPPORT_URL}/en/articles/7231929-minimum-requirements`}
          >
            {'Get help'}
          </Link>
        </>
      )
  },
  [ErrorEnum.WEBVIEW_NOT_SUPPORTED]: {
    id: ErrorEnum.WEBVIEW_NOT_SUPPORTED,
    type: types.error,
    title: titles.error,
      modal: false,
      subTitle: 'Browser version issue',
    children: (
        <>
          <Paragraph>
            {'This Webview is not supported to run GameGlass.'}
            <br/>
            {'Try using a standalone browser.'}
            <br/>
            {'You can find a list of supported browsers on our support page.'}
          </Paragraph>
          <Link
            href={`${SUPPORT_URL}/en/articles/7231929-minimum-requirements`}
          >
            {'Get help'}
          </Link>
        </>
      )
  },
  [ErrorEnum.BROWSER_NOT_SUPPORTED]: {
    id: ErrorEnum.BROWSER_NOT_SUPPORTED,
    type: types.error,
    title: titles.error,
      modal: false,
      subTitle: 'Browser version issue',
    children: (
        <>
          <Paragraph>
            {'Unfortunately, this browser version is not supported by GameGlass.'}
            <br/>
            {'You can find a list of supported browsers on our support page.'}
          </Paragraph>
          <Link
            href={`${SUPPORT_URL}/en/articles/7231929-minimum-requirements`}
          >
            {'Get help'}
          </Link>
        </>
      )
  },
  [ErrorEnum.NOT_OFFICIALLY_SUPPORTED]: {
    id: ErrorEnum.NOT_OFFICIALLY_SUPPORTED,
    type: types.warning,
    title: titles.warning,
    priority: priorities.high,
    modal: true,
    subTitle: 'You may encounter issues on this device',
    children: <NotOfficiallySupported />
  },
  [ErrorEnum.HOST_CONNECTION_ERROR]: {
    id: ErrorEnum.HOST_CONNECTION_ERROR,
    type: types.error,
    title: titles.error,
    priority: priorities.medium,
    modal: true,
    subTitle: 'Cannot connect to host',
    children: <HostConnectionError />
  },
  [ErrorEnum.NO_INTERNET_CONNECTION]: {
    id: ErrorEnum.NO_INTERNET_CONNECTION,
    type: types.error,
    title: titles.error,
    priority: priorities.high,
    modal: true,
    subTitle: 'No internet connection',
    children: <NoInternetConnectionError />
  },
  [ErrorEnum.PROFILE_LOAD_ERROR]: {
    id: ErrorEnum.PROFILE_LOAD_ERROR,
    type: types.error,
    title: titles.error,
    priority: priorities.high,
    modal: true,
    subTitle: 'Cannot load your profile',
    children: <ProfileLoadError />
  },
})

export const getAnnouncement = (type:ErrorEnum) =>
    ANNOUNCEMENTS[type] || ANNOUNCEMENTS[ErrorEnum.UNKNOWN]
